import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { HealtheraButton } from '../../../../healtheraComponents';
import { ClinicMasterProduct } from '../../../../apis/clinic-master-products/types';
import { enqueueSnackbar } from 'notistack';
import { useCreateClinicMasterProduct } from '../../../../apis/clinic-master-products/hooks/useCreateClinicMasterProduct';
import { useHistory } from 'react-router';
import { useCreateClinicProducts } from '../../../../apis/clinic-products/hooks/useCreateClinicProducts';
import { ClinicProduct } from '../../../../apis/clinic-products/types';
import { ValidationError } from 'yup';

interface Props {
  form: UseFormReturn<ClinicMasterProduct>;
  productsForm: UseFormReturn<ClinicProduct[]>;
}

const CreateClinicMasterProduct = ({ form, productsForm }: Props) => {
  const { isLoading, createProducts } = useCreateMasterProduct();

  const handleClick = async () => {
    await form.handleSubmit(createMasterProduct, handleInvalidForm)();
  };

  async function createMasterProduct(masterProduct: ClinicMasterProduct) {
    productsForm.handleSubmit(products => createProducts(masterProduct, products), handleInvalidForm)();
  }

  const handleInvalidForm = error => {
    informAboutError(error);
  };

  return (
    <HealtheraButton disabled={isLoading} onClick={handleClick}>
      Create
    </HealtheraButton>
  );
};

function useCreateMasterProduct() {
  const { createClinicMasterProduct, isLoading } = useCreateClinicMasterProduct();
  const { createClinicProducts } = useCreateClinicProducts();
  const navigateToProductPage = useNavigateToClinicMasterProductPage();

  async function createProducts(masterProduct: ClinicMasterProduct, clinicProducts: ClinicProduct[]) {
    const createdMasterProduct = await createClinicMasterProduct(masterProduct);

    if (createdMasterProduct?._id) {
      await createClinicProducts(createdMasterProduct._id, clinicProducts);

      informAboutSuccess();
      navigateToProductPage(createdMasterProduct.clinic_id, createdMasterProduct._id);
    }
  }

  return { createProducts, isLoading };
}

function useNavigateToClinicMasterProductPage() {
  const history = useHistory();

  return (clinicId: string, clinicMasterProductId?: string) => {
    history.replace(`/clinics/${clinicId}/products/${clinicMasterProductId}`);
  };
}

function informAboutSuccess() {
  enqueueSnackbar('Clinic master product created', { variant: 'success' });
}

function informAboutError(error: ValidationError) {
  if (isNoProductsError(error)) {
    return enqueueSnackbar('This master medicine has no virtual medicines', { variant: 'error' });
  }

  enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
}

function isNoProductsError(error: ValidationError) {
  return error['undefined']?.type === 'min';
}

export default CreateClinicMasterProduct;
